import { useDebounceFn, useEventListener } from '@vueuse/core';
import { useHorizontalShadowManager } from '@/js/helpers/horizontalShadowManager';

type MapType = Map<HTMLButtonElement, HTMLElement>;

enum HeaderVersionEnum {
    MOBILE = 'mobile',
    DESKTOP = 'desktop',
}

const ACTIVE_SECTION_CLASS = 'header-navigation-section--active';
const HEADER_BREAKPOINT = 1200;
const STICKY_HEADER_CLASS = 'header-sticky-component';
const STATIC_NAVBAR_CLASS = 'static-navbar';
const STICKY_HEADER_VISIBLE_CLASS = 'header-sticky-component--visible';
const HEADER_SHADOW_LEFT_CLASS = 'header-shadow--left--active';
const HEADER_SHADOW_RIGHT_CLASS = 'header-shadow--right--active';
const HEADER_STICKY_BOTTOM_CLASS = '.header-sticky-component__bottom';
const HEADER_NAVIGATION_MOBILE_CLASS = '.header-sticky-component__navigation--mobile';

const observableAreas: Record<string, boolean | null> = {};

const getCurrentDisplayStyle = (width: number): HeaderVersionEnum => (width >= HEADER_BREAKPOINT
    ? HeaderVersionEnum.DESKTOP
    : HeaderVersionEnum.MOBILE);

const handleButtonScroll = (button: HTMLButtonElement, scrollTarget: HTMLElement): void => {
    const EXTRA_MOBILE_OFFSET = 20;
    const customOffset = parseInt(button.dataset.customOffset ?? '55', 10);

    const offset = getCurrentDisplayStyle(window.innerWidth) === HeaderVersionEnum.DESKTOP
        ? customOffset
        : customOffset + EXTRA_MOBILE_OFFSET;

    const targetPosition = scrollTarget.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({
        top: targetPosition,
        behavior: 'smooth',
    });
};

const convertToButton = (anchor: HTMLAnchorElement, section: HTMLElement): HTMLButtonElement => {
    const button = document.createElement('button');
    button.className = anchor.className;
    button.innerHTML = anchor.innerHTML;

    Object.assign(button.dataset, anchor.dataset);

    anchor.replaceWith(button);
    useEventListener(button, 'click', () => handleButtonScroll(button, section));

    return button;
};

const bindSectionsToButtons = (): MapType => {
    const navigationButtons = document.querySelectorAll<HTMLAnchorElement>('.header-navigation-section');
    const sectionBindings: MapType = new Map();

    navigationButtons.forEach((anchor) => {
        const sectionSelector = anchor.dataset.bindedSectionSelector;

        if (!sectionSelector) {
            return;
        }

        const section = document.querySelector<HTMLElement>(sectionSelector);

        if (section) {
            const button = convertToButton(anchor, section);
            sectionBindings.set(button, section);
        }
    });

    return sectionBindings;
};

const filterElementsByType = (elements: MapType): MapType => {
    const displayType = getCurrentDisplayStyle(window.innerWidth);
    const filteredElements: MapType = new Map();

    elements.forEach((section, button) => {
        if (button.dataset.type === displayType) {
            filteredElements.set(button, section);
        }
    });

    return filteredElements;
};

const handleActiveSection = (): void => {
    const unfilteredBindings = bindSectionsToButtons();
    const bindings = filterElementsByType(unfilteredBindings);

    let currentActiveButton: HTMLButtonElement | null = null;

    const clearActiveClass = (): void => {
        if (currentActiveButton) {
            currentActiveButton.classList.remove(ACTIVE_SECTION_CLASS);
        }
    };

    const updateActiveButton = (button: HTMLButtonElement | null): void => {
        clearActiveClass();

        if (button) {
            button.classList.add(ACTIVE_SECTION_CLASS);
            currentActiveButton = button;
        }
    };

    bindings.forEach((section) => {
        observableAreas[section.id] = null;
    });

    const handleButtonStateChange = (button: HTMLButtonElement, isActive: boolean): void => {
        if (isActive) {
            updateActiveButton(button);
        } else {
            if (currentActiveButton === button) {
                currentActiveButton = null;
            }

            button.classList.remove(ACTIVE_SECTION_CLASS);
        }
    };

    const calculateRootMargin = (): string => {
        const MOBILE_OFFSET = 100;
        const DESKTOP_OFFSET = 60;
        const offset = getCurrentDisplayStyle(window.innerWidth) === HeaderVersionEnum.DESKTOP
            ? DESKTOP_OFFSET
            : MOBILE_OFFSET;

        return `0px 0px -${window.innerHeight - offset}px 0px`;
    };

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            const targetSection = entry.target as HTMLElement;
            bindings.forEach((section, button) => {
                if (section === targetSection) {
                    observableAreas[section.id] = entry.isIntersecting;
                    handleButtonStateChange(button, entry.isIntersecting);
                }
            });
        });
    }, {
        root: null,
        threshold: 0,
        rootMargin: calculateRootMargin(),
    });

    bindings.forEach((section) => observer.observe(section));
};

const addVisibilityToggle = (): void => {
    const stickyHeader = document.querySelector<HTMLDivElement>(`.${STICKY_HEADER_CLASS}`);
    const staticNavbar = document.querySelector<HTMLDivElement>(`.${STATIC_NAVBAR_CLASS}`);

    if (!stickyHeader) return;

    let isFirstLoad = true;

    const FALLBACK_NAVBAR_OFFSET = 150;
    const getStaticNavbarOffset = (): number => {
        if (!staticNavbar) return FALLBACK_NAVBAR_OFFSET;

        return staticNavbar.offsetTop + staticNavbar.offsetHeight;
    };

    const handleScroll = (): void => {
        const scrollPosition = window.scrollY;

        if (scrollPosition > getStaticNavbarOffset()) {
            stickyHeader.classList.add(STICKY_HEADER_VISIBLE_CLASS);
        } else {
            stickyHeader.classList.remove(STICKY_HEADER_VISIBLE_CLASS);
        }

        if (isFirstLoad) {
            isFirstLoad = false;
            handleScroll();
        }
    };

    useEventListener(window, 'scroll', handleScroll);
};

const handleWindowResize = (): void => {
    const debouncedResizeHandler = useDebounceFn(() => {
        handleActiveSection();
    }, 100);

    useEventListener(window, 'resize', debouncedResizeHandler);
};

useEventListener(document, 'DOMContentLoaded', () => {
    useHorizontalShadowManager(
        HEADER_STICKY_BOTTOM_CLASS,
        HEADER_NAVIGATION_MOBILE_CLASS,
        HEADER_SHADOW_LEFT_CLASS,
        HEADER_SHADOW_RIGHT_CLASS,
        HEADER_BREAKPOINT,
    );

    handleWindowResize();
    handleActiveSection();
    addVisibilityToggle();
});
